import { useState, useEffect } from "react";
import { getVendors } from "../../api/vendorApi"; // Fixed import path

const API_BASE_URL = "https://ims.holdthechild.net/api";

const ProposalForm = ({ rfq, onClose, onSuccess }) => {
  const [vendorName, setVendorName] = useState("");
  const [vendorCode, setVendorCode] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [proposalSummary, setProposalSummary] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const [items, setItems] = useState([]);
  const [deliveryTimeline, setDeliveryTimeline] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [validityPeriod, setValidityPeriod] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // New states for vendor dropdown
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [error, setError] = useState(null);

  // Debug: Log the rfq prop
  useEffect(() => {
    console.log("ProposalForm received rfq:", rfq);
    console.log("RFQ type:", rfq?.type);
  }, [rfq]);

  // Fetch vendors on component mount
  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Authentication token not found. Please log in.");
          return;
        }

        console.log("Fetching vendors...");
        const response = await getVendors();
        console.log("Vendors response:", response);
        
        // Handle different possible response structures
        let vendorData = [];
        if (Array.isArray(response)) {
          vendorData = response;
        } else if (response?.data) {
          vendorData = Array.isArray(response.data) ? response.data : response.data.data || [];
        }
        
        console.log("Processed vendor data:", vendorData);
        setVendors(Array.isArray(vendorData) ? vendorData : []);
      } catch (error) {
        console.error("Failed to load vendors:", error);
        setError(`Failed to load vendors: ${error.message}`);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendors();
  }, []);

  // Initialize items based on RFQ type
  useEffect(() => {
    if (rfq?.type === "RFQ" || rfq?.type === "BOQ") {
      // Initialize with one empty item if none exist
      if (items.length === 0) {
        setItems([
          { description: "", quantity: 0, uom: "", unitPrice: 0, vat: 0 }
        ]);
      }
    }
  }, [rfq?.type, items.length]);

  // Handle vendor selection
  const handleVendorChange = (e) => {
    const selectedVendorId = e.target.value;
    setSelectedVendor(selectedVendorId);
    
    if (selectedVendorId) {
      const vendor = vendors.find(v => v._id === selectedVendorId);
      if (vendor) {
        console.log("Selected vendor:", vendor);
        setVendorName(vendor.name || "");
        setVendorCode(vendor.vendorCode || vendor.code || "");
        setContactPerson(vendor.contactPerson || vendor.contactName || "");
        setContactEmail(vendor.email || vendor.contactEmail || "");
      }
    } else {
      // Clear fields if no vendor selected
      setVendorName("");
      setVendorCode("");
      setContactPerson("");
      setContactEmail("");
    }
  };

  const handleFileChange = (e) => setFiles([...e.target.files]);

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const addNewItem = () => {
    setItems([
      ...items,
      { description: "", quantity: 0, uom: "", unitPrice: 0, vat: 0 },
    ]);
  };

  const removeItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
  };

  const calculateTotalFromItems = () => {
    return items.reduce((total, item) => {
      const subtotal = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
      const vatAmount = subtotal * (parseFloat(item.vat || 0) / 100);
      return total + subtotal + vatAmount;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!selectedVendor) {
        alert("Please select a vendor");
        return;
      }

      // Build proposal data based on type
      const proposalData = {
        reference: {
          id: rfq._id,
          type: rfq.type || "RFQ",
        },
        vendorId: selectedVendor, // Include vendor ID
        vendorName,
        vendorCode,
        contactPerson,
        contactEmail,
        status: "submitted",
        deliveryTimeline,
        paymentTerms,
        validityPeriod: validityPeriod
          ? new Date(validityPeriod).toISOString()
          : null,
        attachedDocs: [], // File upload not implemented yet
        submissionDate: new Date().toISOString(),
      };

      // Add type-specific fields
      if (rfq.type === "RFP") {
        proposalData.proposalSummary = proposalSummary;
        proposalData.totalCost = parseFloat(totalCost) || 0;
        proposalData.totalAmount = parseFloat(totalCost) || 0; // Some APIs might expect this field name
      } else if (rfq.type === "RFQ" || rfq.type === "BOQ") {
        // Validate items
        const validItems = items.filter(item => 
          item.description && 
          parseFloat(item.quantity) > 0 && 
          parseFloat(item.unitPrice) > 0
        );
        
        if (validItems.length === 0) {
          alert("Please add at least one valid item with description, quantity, and unit price");
          return;
        }
        
        proposalData.items = validItems.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          vat: parseFloat(item.vat || 0),
          subtotal: parseFloat(item.quantity) * parseFloat(item.unitPrice),
          vatAmount: (parseFloat(item.quantity) * parseFloat(item.unitPrice)) * (parseFloat(item.vat || 0) / 100)
        }));
        
        // Calculate total amount from items
        const calculatedTotal = calculateTotalFromItems();
        proposalData.totalAmount = calculatedTotal;
        proposalData.totalCost = calculatedTotal; // Some APIs might expect this field name
      }

      console.log("Submitting proposal data:", proposalData);

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/vendor-submissions`, {
        method: 'POST',
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(proposalData)
      });

      const responseData = await response.json();
      console.log("Submission response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.message || responseData.error || "Submission failed");
      }

      alert(`${rfq.type} submission successful!`);
      onSuccess();
    } catch (err) {
      console.error("Submission error:", err);
      alert(
        `Error submitting ${rfq.type} proposal: ${err.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no rfq
  if (!rfq) {
    return (
      <div className="p-6">
        <div className="text-red-600">Error: No RFQ data provided</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <h3 className="text-lg font-semibold text-gray-700">
        Submit {rfq.type} Submission for "{rfq.projectName || rfq.title || 'Untitled'}"
      </h3>

      {/* Show error if authentication fails */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-blue-50 border border-blue-200 text-blue-600 px-3 py-2 rounded text-xs">
          <strong>Debug:</strong> Type: {rfq.type} | Vendors loaded: {vendors.length} | Items: {items.length}
        </div>
      )}

      {/* Vendor info with dropdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Vendor Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Vendor *</label>
          <select
            value={selectedVendor}
            onChange={handleVendorChange}
            required
            disabled={loadingVendors}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="">
              {loadingVendors ? "Loading vendors..." : "Select Vendor"}
            </option>
            {Array.isArray(vendors) && vendors.map((vendor) => (
              <option key={vendor._id} value={vendor._id}>
                {vendor.name} {vendor.vendorCode || vendor.code ? `(${vendor.vendorCode || vendor.code})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Auto-populated Vendor Code (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Code</label>
          <input
            type="text"
            placeholder="Vendor Code (auto-filled)"
            value={vendorCode}
            readOnly
            className="w-full border p-2 rounded-lg bg-gray-100 text-gray-600"
            title="This field is automatically filled when you select a vendor"
          />
        </div>

        {/* Contact fields - can be auto-populated or manually edited */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
          <input
            type="text"
            required
            placeholder="Contact Person"
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
          <input
            type="email"
            required
            placeholder="Contact Email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
      </div>

      {/* Type-specific content */}
      {rfq.type === "RFP" ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Summary</label>
            <textarea
              placeholder="Describe your proposal..."
              value={proposalSummary}
              onChange={(e) => setProposalSummary(e.target.value)}
              className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
              rows={4}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost *</label>
            <input
              type="number"
              step="0.01"
              required
              placeholder="Total Cost"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </>
      ) : (
        /* RFQ and BOQ items */
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">Items *</label>
            <button
              type="button"
              onClick={addNewItem}
              className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded transition-colors text-sm"
            >
              + Add Item
            </button>
          </div>
          
          {items.map((item, index) => (
            <div key={index} className="border p-3 rounded-lg space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Item {index + 1}</span>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                <input
                  type="text"
                  placeholder="Description *"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, "description", e.target.value)}
                  className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Quantity *"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                  className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  required
                />
                <input
                  type="text"
                  placeholder="UOM"
                  value={item.uom}
                  onChange={(e) => handleItemChange(index, "uom", e.target.value)}
                  className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Unit Price *"
                  value={item.unitPrice}
                  onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                  className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  required
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="VAT %"
                  value={item.vat}
                  onChange={(e) => handleItemChange(index, "vat", e.target.value)}
                  className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              {/* Show calculated subtotal */}
              {item.quantity && item.unitPrice && (
                <div className="text-sm text-gray-600">
                  Subtotal: ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0) * (1 + (parseFloat(item.vat) || 0) / 100)).toFixed(2)}
                </div>
              )}
            </div>
          ))}
          
          {/* Show total calculated amount */}
          {items.some(item => item.quantity && item.unitPrice) && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-lg font-semibold text-gray-800">
                Total Amount: ${calculateTotalFromItems().toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* General info */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Timeline</label>
          <input
            type="text"
            placeholder="e.g., 2 weeks"
            value={deliveryTimeline}
            onChange={(e) => setDeliveryTimeline(e.target.value)}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
          <input
            type="text"
            placeholder="Payment Terms"
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Validity Period</label>
          <input
            type="date"
            placeholder="Validity Period"
            value={validityPeriod}
            onChange={(e) => setValidityPeriod(e.target.value)}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
      </div>

      {/* File upload placeholder */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          className="w-full border p-2 rounded-lg bg-gray-100"
          disabled
          title="File upload feature coming soon"
        />
        <p className="text-xs text-gray-500 mt-1">File upload feature coming soon</p>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !selectedVendor || loadingVendors}
          className="px-6 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? "Submitting..." : `Submit ${rfq.type}`}
        </button>
      </div>
    </form>
  );
};

export default ProposalForm;